import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Copy, Download, Loader2, User, Sun, Moon, Star, Sparkles, Hash,
  ZoomIn, ZoomOut, Maximize2, Minimize2, RotateCcw, ChevronLeft, ChevronRight,
  Filter, BarChart3, Clock, Shield,
} from 'lucide-react';
import NorthIndianChart from '../components/charts/NorthIndianChart';
import NakshatraWheel from '../components/charts/NakshatraWheel';
import { PlanetPopup, LagnaPopup, HousePopup, NakshatraPopup } from '../components/charts/Popups';
import CurrentDashaCard from '../components/dasha/CurrentDashaCard';
import DashaTimeline from '../components/dasha/DashaTimeline';
import DashaTables from '../components/dasha/DashaTables';
import BadhakaCard from '../components/dasha/BadhakaCard';
import { DashaPopup, BadhakaPopup } from '../components/dasha/DashaPopup';
import TransitTable from '../components/transit/TransitTable';
import TransitAlerts from '../components/transit/TransitAlerts';
import TransitPopup from '../components/transit/TransitPopup';
import TimelineView from '../components/timeline/TimelineView';
import PlanetResearchPanel from '../components/timeline/PlanetResearchPanel';
import TimelineEventPopup from '../components/timeline/TimelineEventPopup';
import Phase3Section from '../components/astronomy/Phase3Cards';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const PLANETS = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
const PLANET_HI = {
  Sun: 'सूर्य', Moon: 'चंद्र', Mars: 'मंगल', Mercury: 'बुध',
  Jupiter: 'गुरु', Venus: 'शुक्र', Saturn: 'शनि', Rahu: 'राहु', Ketu: 'केतु',
};
const SIGN_HI = ['मेष', 'वृषभ', 'मिथुन', 'कर्क', 'सिंह', 'कन्या', 'तुला', 'वृश्चिक', 'धनु', 'मकर', 'कुंभ', 'मीन'];

const SECTIONS = [
  { id: 'birth',    label: '🪔 जन्म + पंचांग' },
  { id: 'sun-intelligence',   label: '☀ सूर्य Intelligence' },
  { id: 'moon-intelligence',  label: '🌙 चंद्र Intelligence' },
  { id: 'planet-premium',     label: '✨ Premium Planet Summary' },
  { id: 'charts',   label: '🕉 वर्ग कुंडलियाँ (D1/D9/...)' },
  { id: 'chandra',  label: '🌙 चंद्र कुंडली' },
  { id: 'transit',  label: '🌌 गोचर चक्र' },
  { id: 'wheel',    label: '🌀 नक्षत्र चक्र' },
  { id: 'planets',  label: '🪐 ग्रह सारणी' },
  { id: 'strength', label: '💪 Basic Strength (Experimental)' },
  { id: 'compare',  label: '⚖ D1 vs D9' },
  { id: 'status',   label: '⚡ ग्रह स्थिति' },
  { id: 'relations',label: '🤝 ग्रह मैत्री' },
  { id: 'bhavas',   label: '🏠 भाव' },
  { id: 'dasha',    label: '🕰 दशा' },
  { id: 'badhaka',  label: '🛡 बाधक' },
  { id: 'timeline', label: '📜 5-वर्ष टाइमलाइन' },
];

// Build chart-ready planet info from kundli data + varga sign mapping
function buildChartPlanets(data, vargaCode) {
  if (!data || !data.varga_charts || !data.varga_charts[vargaCode]) return { lagnaSign: 0, houseSigns: [], planets: {} };
  const v = data.varga_charts[vargaCode];
  const lagnaSign = v.lagna_sign;
  const houseSigns = Array.from({ length: 12 }, (_, i) => (lagnaSign + i) % 12);
  const planets = {};
  for (const nm of PLANETS) {
    const sIdx = v.planets[nm];
    if (sIdx === undefined) continue;
    const popup = data.popup_data?.[nm] || {};
    const status = popup.status || {};
    planets[nm] = {
      sign: sIdx,
      deg: vargaCode === 'D1' ? popup.degree : undefined, // only show degree in D1
      retro: status.retrograde,
      combust: status.combust,
      exalted: vargaCode === 'D1' ? status.exalted : false,
      debilitated: vargaCode === 'D1' ? status.debilitated : false,
      vargottama: vargaCode === 'D9' ? status.vargottama : false,
    };
  }
  return { lagnaSign, houseSigns, planets };
}

export default function ClientKundliPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Chart state
  const [activeVarga, setActiveVarga] = useState('D1');
  const [filteredPlanets, setFilteredPlanets] = useState(new Set(PLANETS));
  const [zoom, setZoom] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);

  // Popup state
  const [openPopup, setOpenPopup] = useState(null);   // { type: 'planet'|'lagna'|'house'|'nakshatra', payload }

  // Mobile swipe state
  const [swipeIdx, setSwipeIdx] = useState(0);
  const touchStartX = useRef(null);

  // Transit filter (Phase B4)
  const [transitFilter, setTransitFilter] = useState('all');

  // Timeline state (Phase B5 — lazy loaded)
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState('');
  const [timelineYears, setTimelineYears] = useState(5);
  const [snapshotMode, setSnapshotMode] = useState(null); // { date, time, data }
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  const loadTimeline = async (years = timelineYears) => {
    setTimelineLoading(true);
    setTimelineError('');
    try {
      const res = await axios.get(`${API}/api/acharya/clients/${id}/timeline`, {
        params: { years_forward: years, years_back: 0 },
        headers: headers(),
        timeout: 60000,
      });
      setTimeline(res.data);
    } catch (e) {
      setTimelineError(e?.response?.data?.detail || 'Timeline लोड नहीं हो सकी');
    } finally {
      setTimelineLoading(false);
    }
  };

  const loadResearchSnapshot = async (dateStr, timeStr) => {
    setSnapshotLoading(true);
    try {
      const snapTz = data?.birth_data?.location_details?.timezone || 'Asia/Kolkata';
      const res = await axios.get(`${API}/api/acharya/clients/${id}/kundli`, {
        params: { snapshot_date: dateStr, snapshot_time: timeStr, snapshot_tz: snapTz },
        headers: headers(),
        timeout: 30000,
      });
      setSnapshotMode({ date: dateStr, time: timeStr, data: res.data.snapshot });
      setOpenPopup(null);
    } catch (e) {
      setTimelineError(e?.response?.data?.detail || 'Snapshot लोड नहीं हो सका');
    } finally {
      setSnapshotLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [cRes, kRes] = await Promise.all([
          axios.get(`${API}/api/acharya/clients/${id}`, { headers: headers() }),
          axios.get(`${API}/api/acharya/clients/${id}/kundli`, { headers: headers() }),
        ]);
        setClient(cRes.data);
        setData(kRes.data);
      } catch (e) {
        if (e?.response?.status === 401 || e?.response?.status === 403) navigate('/admin/login');
        else setError(e?.response?.data?.detail || 'Could not load');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const vargaOrder = data?.varga_order || ['D1', 'D9', 'D10', 'D7'];

  const copy = (txt) => navigator.clipboard.writeText(txt);
  const exportJson = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${client?.name?.replace(/\s+/g, '_') || 'kundli'}_${client?.dob || ''}.json`;
    a.click(); URL.revokeObjectURL(url);
  };

  const togglePlanet = (nm) => {
    const next = new Set(filteredPlanets);
    if (next.has(nm)) next.delete(nm); else next.add(nm);
    setFilteredPlanets(next);
  };

  // Mobile swipe handlers
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 60) {
      const dir = dx < 0 ? 1 : -1;
      setSwipeIdx((i) => Math.max(0, Math.min(vargaOrder.length - 1, i + dir)));
      setActiveVarga(vargaOrder[Math.max(0, Math.min(vargaOrder.length - 1, swipeIdx + dir))]);
    }
    touchStartX.current = null;
  };

  const openPlanet = (nm) => setOpenPopup({ type: 'planet', payload: nm });
  const openLagna = () => setOpenPopup({ type: 'lagna' });
  const openHouse = (n) => setOpenPopup({ type: 'house', payload: n });
  const openNakByIdx = (idx) => setOpenPopup({ type: 'nakshatra', payload: { idx } });
  const openNakByName = (name) => setOpenPopup({ type: 'nakshatra', payload: { name } });
  const closePopup = () => setOpenPopup(null);

  // Chart size (depends on zoom + fullscreen)
  const baseSize = fullscreen ? 560 : 320;
  const chartSize = Math.round(baseSize * zoom);

  // Tablet/Desktop: 2 charts side-by-side (D1 + D9) and on Desktop full grid we show 4
  // We expose:
  //  - one chart on mobile (active varga + swipe)
  //  - two on tablet (D1 | D9)
  //  - four on desktop (D1 | D9 | active | active)
  // But user said grid: D1 | D9 // Transit | Varsha. Since transit/varsha not built yet, fallback to D1/D9/D10/D7.

  const d1 = useMemo(() => buildChartPlanets(data, 'D1'), [data]);
  const d9 = useMemo(() => buildChartPlanets(data, 'D9'), [data]);
  const dx = useMemo(() => buildChartPlanets(data, activeVarga), [data, activeVarga]);

  if (loading) return <div className="min-h-screen bg-bg-primary flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>;
  if (error) return <div className="min-h-screen bg-bg-primary flex items-center justify-center text-red-300 p-6">{error}</div>;
  if (!data) return null;

  return (
    <div className={`min-h-screen bg-bg-primary text-text-main overflow-x-hidden ${fullscreen ? 'overflow-hidden' : ''}`}>
      <div className="sticky top-0 z-30 border-b border-gold/20 bg-gradient-to-r from-purple-950/50 via-bg-primary to-purple-950/50 backdrop-blur-md">
        <div className="container-custom flex items-center justify-between py-4">
          <button onClick={() => navigate('/acharya-dashboard')} data-testid="back-btn" className="inline-flex items-center gap-2 text-text-muted hover:text-gold">
            <ArrowLeft className="w-4 h-4" /> वापस
          </button>
          <div className="text-center">
            <h1 className="font-heading text-xl text-gold">{client?.name}</h1>
            <p className="text-xs text-text-muted">{client?.dob} · {client?.tob} · {client?.pob}</p>
          </div>
          <button onClick={exportJson} data-testid="export-json-btn" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/15 border border-gold/30 text-gold hover:bg-gold/25 text-sm">
            <Download className="w-3.5 h-3.5" /> JSON
          </button>
        </div>
        {/* Section nav */}
        <div className="container-custom flex gap-2 overflow-x-auto pb-3">
          {SECTIONS.map(s => (
            <a key={s.id} href={`#${s.id}`} className="text-xs whitespace-nowrap px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-text-muted hover:text-gold">{s.label}</a>
          ))}
        </div>
      </div>

      <div className="container-custom py-8 space-y-6">
        {/* SECTION 1 — Birth + Panchang */}
        <Card id="birth" icon={User} title="जन्म डेटा + पंचांग" testid="card-birth">
          <Grid>
            <Row label="नाम" value={data.birth_data.name} onCopy={copy} />
            <Row label="जन्म तिथि" value={data.birth_data.dob} onCopy={copy} />
            <Row label="जन्म समय" value={data.birth_data.tob} onCopy={copy} />
            <Row label="जन्म स्थान" value={data.birth_data.pob} onCopy={copy} />
          </Grid>

          {/* Phase 0 — Complete Location Card (worldwide, never blank) */}
          {(() => {
            const loc = data.birth_data.location_details || {};
            const lat = loc.latitude ?? data.birth_data.latitude;
            const lng = loc.longitude ?? data.birth_data.longitude;
            const tz = loc.timezone || (typeof data.birth_data.timezone === 'number' ? null : data.birth_data.timezone);
            const utcOffset = loc.utc_offset || (typeof data.birth_data.timezone === 'number'
              ? `UTC ${data.birth_data.timezone >= 0 ? '+' : ''}${String(Math.floor(Math.abs(data.birth_data.timezone))).padStart(2,'0')}:${String(Math.round((Math.abs(data.birth_data.timezone) % 1) * 60)).padStart(2,'0')}`
              : null);
            return (
              <>
                <Divider />
                <div className="flex items-center justify-between mb-3 px-1">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-gold/80 font-semibold">स्थान विवरण · Location</p>
                  {loc.confidence && (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${loc.confidence >= 0.9 ? 'bg-green-500/10 border-green-500/30 text-green-300' : loc.confidence >= 0.7 ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-300' : 'bg-orange-500/10 border-orange-500/30 text-orange-300'}`}>
                      {Math.round(loc.confidence * 100)}% • {loc.source}
                    </span>
                  )}
                </div>
                <Grid>
                  {loc.city && <Row label="शहर / City" value={loc.city} onCopy={copy} />}
                  {loc.district && <Row label="जिला / District" value={loc.district} />}
                  {loc.state && <Row label="राज्य / State" value={loc.state} />}
                  {loc.country && <Row label="देश / Country" value={loc.country} />}
                  {loc.postcode && <Row label="पिन कोड / Postal" value={loc.postcode} />}
                  <Row label="अक्षांश (Lat)" value={lat != null ? Number(lat).toFixed(4) : 'गणना में…'} onCopy={lat != null ? copy : undefined} />
                  <Row label="देशांतर (Lng)" value={lng != null ? Number(lng).toFixed(4) : 'गणना में…'} onCopy={lng != null ? copy : undefined} />
                  {loc.elevation != null && <Row label="ऊँचाई / Elevation" value={`${Math.round(loc.elevation)} m`} />}
                  <Row label="Timezone" value={tz ? `${tz}${utcOffset ? ` (${utcOffset})` : ''}` : 'गणना में…'} />
                  {loc.dst_applied !== undefined && loc.dst_applied !== null && (
                    <Row label="DST" value={loc.dst_applied ? 'Yes (Daylight Saving)' : 'No'} />
                  )}
                  <Row label="अयनांश (Lahiri)" value={data.birth_data.ayanamsa} onCopy={copy} highlight />
                </Grid>
              </>
            );
          })()}

          <Divider />
          <Grid>
            <Row label="सूर्योदय" value={data.panchang.sunrise} />
            <Row label="सूर्यास्त" value={data.panchang.sunset} />
            <Row label="वार" value={data.panchang.weekday} />
            <Row label="तिथि" value={data.panchang.tithi} />
            <Row label="पक्ष" value={data.panchang.paksha} />
            <Row label="योग" value={data.panchang.yoga} />
            <Row label="करण" value={data.panchang.karana} />
          </Grid>
        </Card>

        {/* PHASE 1 — Birth Snapshot (at-a-glance summary) */}
        {data.birth_snapshot && (
          <Card id="snapshot" icon={Star} title="जन्म स्नैपशॉट · Birth Snapshot" testid="card-birth-snapshot">
            <div className="grid sm:grid-cols-3 gap-4">
              {/* Lagna pillar */}
              <div className="bg-gold/5 border border-gold/20 rounded-xl p-4">
                <p className="text-[11px] uppercase tracking-[0.2em] text-gold/80 mb-3">लग्न / Ascendant</p>
                <p className="text-2xl font-heading text-gold mb-1">{data.birth_snapshot.lagna?.sign}</p>
                <p className="text-xs text-text-muted">{data.birth_snapshot.lagna?.degree}</p>
                <p className="text-[11px] text-text-muted mt-2">स्वामी: <span className="text-text-main">{data.birth_snapshot.lagna?.lord}</span></p>
              </div>
              {/* Moon pillar */}
              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4">
                <p className="text-[11px] uppercase tracking-[0.2em] text-blue-300/80 mb-3">चंद्र / Moon</p>
                <p className="text-2xl font-heading text-blue-200 mb-1">{data.birth_snapshot.moon?.sign}</p>
                <p className="text-xs text-text-muted">{data.birth_snapshot.moon?.nakshatra || data.panchang?.nakshatra} · पाद {data.birth_snapshot.moon?.pada}</p>
                <p className="text-[11px] text-text-muted mt-2">स्वामी: <span className="text-text-main">{data.birth_snapshot.moon?.lord}</span> · नक्षत्र स्वामी: <span className="text-text-main">{data.birth_snapshot.moon?.nakshatra_lord}</span></p>
              </div>
              {/* Sun pillar */}
              <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-4">
                <p className="text-[11px] uppercase tracking-[0.2em] text-orange-300/80 mb-3">सूर्य / Sun</p>
                <p className="text-2xl font-heading text-orange-200 mb-1">{data.birth_snapshot.sun?.sign}</p>
                <p className="text-[11px] text-text-muted mt-2">स्वामी: <span className="text-text-main">{data.birth_snapshot.sun?.lord}</span></p>
              </div>
            </div>
            <Divider />
            <Grid>
              <Row label="वार · Weekday" value={data.birth_snapshot.panchang?.weekday} />
              <Row label="वार स्वामी" value={data.birth_snapshot.panchang?.weekday_lord} />
              <Row label="तिथि" value={data.birth_snapshot.panchang?.tithi} />
              <Row label="तिथि स्वामी" value={data.birth_snapshot.panchang?.tithi_lord} />
              <Row label="पक्ष" value={data.birth_snapshot.panchang?.paksha} />
              <Row label="योग" value={data.birth_snapshot.panchang?.yoga} />
              <Row label="योग स्वामी" value={data.birth_snapshot.panchang?.yoga_lord} />
              <Row label="करण" value={data.birth_snapshot.panchang?.karana} />
              <Row label="करण स्वामी" value={data.birth_snapshot.panchang?.karana_lord} />
            </Grid>
            <Divider />
            <Grid>
              <Row label="चान्द्र मास" value={data.birth_snapshot.calendar?.lunar_month} />
              <Row label="अयन" value={data.birth_snapshot.calendar?.ayan} />
              <Row label="ऋतु" value={data.birth_snapshot.calendar?.ritu} />
              <Row label="संवत्सर" value={data.birth_snapshot.calendar?.samvatsara} />
              <Row label="विक्रम संवत" value={data.birth_snapshot.calendar?.vikram_samvat} />
              <Row label="शक संवत" value={data.birth_snapshot.calendar?.shaka_samvat} />
              <Row label="कलियुग वर्ष" value={data.birth_snapshot.calendar?.kaliyuga_year} />
            </Grid>
          </Card>
        )}

        {/* PHASE 1 — Expanded Panchang */}
        {data.panchang && (
          <Card id="panchang-expanded" icon={Clock} title="विस्तृत पंचांग · Expanded Panchang" testid="card-panchang-expanded">
            <Grid>
              <Row label="वार" value={data.panchang.weekday} />
              <Row label="वार स्वामी" value={data.panchang.weekday_lord} />
              <Row label="तिथि" value={`${data.panchang.tithi || '—'}${data.panchang.tithi_completion_pct != null ? `  (${data.panchang.tithi_completion_pct}%)` : ''}`} />
              <Row label="तिथि स्वामी" value={data.panchang.tithi_lord} />
              <Row label="तिथि पूर्णता" value={data.panchang.tithi_completion_pct != null ? `${data.panchang.tithi_completion_pct}% (शेष ${data.panchang.tithi_remaining_pct}%)` : '—'} />
              <Row label="पक्ष" value={data.panchang.paksha} />
              <Row label="योग" value={data.panchang.yoga} />
              <Row label="योग स्वामी" value={data.panchang.yoga_lord} />
              <Row label="योग पूर्णता" value={data.panchang.yoga_completion_pct != null ? `${data.panchang.yoga_completion_pct}%` : '—'} />
              <Row label="करण" value={data.panchang.karana} />
              <Row label="करण स्वामी" value={data.panchang.karana_lord} />
              {data.panchang.karana_start_time && <Row label="करण प्रारंभ" value={data.panchang.karana_start_time} />}
              {data.panchang.karana_end_time && <Row label="करण समाप्ति" value={data.panchang.karana_end_time} />}
            </Grid>
            <Divider />
            <Grid>
              <Row label="सूर्योदय" value={data.panchang.sunrise} />
              <Row label="सूर्यास्त" value={data.panchang.sunset} />
              <Row label="मध्याह्न (Solar Noon)" value={data.panchang.solar_noon} />
              <Row label="दिनमान · Day Duration" value={data.panchang.day_duration} />
              <Row label="रात्रिमान · Night Duration" value={data.panchang.night_duration} />
              <Row label="चंद्र आयु (दिन)" value={data.panchang.moon_age_days} />
            </Grid>
            <Divider />
            <Grid>
              <Row label="विक्रम संवत" value={data.panchang.vikram_samvat} />
              <Row label="शक संवत" value={data.panchang.shaka_samvat} />
              <Row label="कलियुग वर्ष" value={data.panchang.kaliyuga_year} />
              <Row label="चान्द्र मास" value={data.panchang.masa} />
              <Row label="ऋतु" value={data.panchang.ritu} />
              <Row label="अयन" value={data.panchang.ayana} />
              <Row label="संवत्सर" value={data.panchang.samvatsara} />
            </Grid>
          </Card>
        )}

        {/* PHASE 2 — Premium Nakshatra Module */}
        {data.nakshatra_module && (
          <Card id="nakshatra-premium" icon={Star} title="नक्षत्र मॉड्यूल · Premium Nakshatra" testid="card-nakshatra-premium">
            <div className="text-center mb-4">
              <p className="text-[11px] uppercase tracking-[0.22em] text-gold/70">{data.nakshatra_module.name_en}</p>
              <p className="font-heading text-4xl text-gold mt-1">{data.nakshatra_module.name_hi}</p>
              <p className="text-text-muted text-sm mt-1">पाद {data.nakshatra_module.pada} · स्वामी {data.nakshatra_module.lord} · पाद स्वामी {data.nakshatra_module.pada_lord}</p>
            </div>
            <Grid>
              <Row label="देवता" value={data.nakshatra_module.devata} />
              <Row label="प्रतीक" value={data.nakshatra_module.symbol} />
              <Row label="वृक्ष" value={data.nakshatra_module.tree} />
              <Row label="योनि (पशु)" value={data.nakshatra_module.animal} />
              <Row label="गण" value={data.nakshatra_module.gana} />
              <Row label="नाड़ी" value={data.nakshatra_module.nadi} />
              <Row label="रज्जु" value={data.nakshatra_module.rajju} />
              <Row label="वर्ण" value={data.nakshatra_module.varna} />
              <Row label="वश्य" value={data.nakshatra_module.vashya} />
              <Row label="तत्व" value={data.nakshatra_module.element} />
              <Row label="दिशा" value={data.nakshatra_module.direction} />
            </Grid>
            <p className="text-[10px] text-text-muted/60 mt-3">स्रोत: {(data.nakshatra_module.shastra_refs || []).join(' · ')}</p>
          </Card>
        )}

        {/* PHASE 2 — Naam-akshar */}
        {data.naam_akshar && (
          <Card id="naam-akshar" icon={User} title="नामाक्षर · Recommended Naming Syllables" testid="card-naam-akshar">
            <div className="text-center mb-5">
              <p className="text-[11px] uppercase tracking-[0.22em] text-gold/70 mb-2">आपका नामाक्षर</p>
              <p className="font-heading text-6xl text-gold">{data.naam_akshar.user_syllable}</p>
              <p className="text-text-muted text-xs mt-2">{data.naam_akshar.nakshatra_name} · पाद {data.naam_akshar.user_pada}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-center">
                <thead><tr className="border-b border-white/10 text-text-muted text-xs uppercase tracking-wider">
                  <th className="py-2 pl-2 text-left">नक्षत्र</th>
                  <th>पाद 1</th><th>पाद 2</th><th>पाद 3</th><th>पाद 4</th>
                </tr></thead>
                <tbody>
                  {data.naam_akshar.grid.map((row, i) => (
                    <tr key={i} className={`border-b border-white/5 ${i === data.naam_akshar.user_nak_idx ? 'bg-gold/10' : ''}`}>
                      <td className="py-1.5 pl-2 text-left text-text-muted">{i+1}. {/* nakshatra name from server */ data.naam_akshar.grid_names?.[i] || ''}</td>
                      {row.map((s, j) => (
                        <td key={j} className={`py-1.5 ${i === data.naam_akshar.user_nak_idx && j+1 === data.naam_akshar.user_pada ? 'text-gold font-semibold ring-1 ring-gold/40 rounded' : 'text-text-main'}`}>{s}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-text-muted/60 mt-3">स्रोत: {data.naam_akshar.shastra_ref}</p>
          </Card>
        )}

        {/* PHASE 2 — Paaya */}
        {data.paaya && (
          <Card id="paaya" icon={Star} title="पाया · Paaya" testid="card-paaya">
            <p className="font-heading text-3xl text-gold text-center mb-2">{data.paaya.paaya}</p>
            <p className="text-text-muted text-sm text-center mb-4">सूर्य से चंद्र की गिनती: {data.paaya.count_from_sun}</p>
            <p className="text-text-main text-sm text-center px-2">{data.paaya.interpretation_hi}</p>
            <p className="text-[10px] text-text-muted/60 mt-3 text-center">स्रोत: {data.paaya.shastra_ref}</p>
          </Card>
        )}

        {/* PHASE 2 — Tarabala + Chandrabala */}
        {(data.tarabala || data.chandrabala) && (
          <Card id="bala" icon={Star} title="तारबल + चंद्रबल · Bala (आज की स्थिति)" testid="card-bala">
            {data.tarabala && (
              <div className="mb-4">
                <p className="text-text-muted text-xs mb-2">तारबल — संदर्भ तिथि: {data.tarabala.reference_date}</p>
                <p className={`font-heading text-xl ${data.tarabala.auspicious ? 'text-green-400' : 'text-yellow-400'}`}>
                  {data.tarabala.tara_name} ({data.tarabala.tara_num}/9)
                </p>
                <p className="text-text-muted text-sm mt-1">{data.tarabala.interpretation_hi}</p>
                <p className="text-[10px] text-text-muted/60 mt-2">{data.tarabala.shastra_ref}</p>
              </div>
            )}
            {data.chandrabala && (
              <div>
                <Divider />
                <p className="text-text-muted text-xs mb-2">चंद्रबल — संदर्भ तिथि: {data.chandrabala.reference_date}</p>
                <p className={`font-heading text-xl ${data.chandrabala.auspicious ? 'text-green-400' : 'text-yellow-400'}`}>
                  गिनती {data.chandrabala.count_from_birth} ({data.chandrabala.tier})
                </p>
                <p className="text-[10px] text-text-muted/60 mt-2">{data.chandrabala.shastra_ref}</p>
              </div>
            )}
          </Card>
        )}

        {/* PHASE 2 — Special Birth Conditions */}
        {(data.gandmool || data.panchak || data.bhadra || data.abhijit_muhurta || data.hora) && (
          <Card id="special-conditions" icon={Star} title="विशेष जन्म स्थितियाँ · Special Conditions" testid="card-special-conditions">
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              {data.gandmool && (
                <div className={`rounded-lg p-3 border ${data.gandmool.is_gandmool ? 'bg-red-500/10 border-red-500/30' : 'bg-white/3 border-white/8'}`}>
                  <p className="text-text-muted text-[11px] uppercase">गंडमूल</p>
                  <p className="text-text-main">{data.gandmool.is_gandmool ? `हाँ — ${data.gandmool.nakshatra}` : 'नहीं'}</p>
                  {data.gandmool.remedy && <p className="text-text-muted text-[11px] mt-1">{data.gandmool.remedy}</p>}
                  <p className="text-[10px] text-text-muted/60 mt-1">स्रोत: {data.gandmool.shastra_ref}</p>
                </div>
              )}
              {data.panchak && (
                <div className={`rounded-lg p-3 border ${data.panchak.is_panchak ? 'bg-red-500/10 border-red-500/30' : 'bg-white/3 border-white/8'}`}>
                  <p className="text-text-muted text-[11px] uppercase">पंचक</p>
                  <p className="text-text-main">{data.panchak.is_panchak ? 'हाँ' : 'नहीं'}</p>
                  {data.panchak.is_panchak && <p className="text-text-muted text-[10px] mt-1">वर्जित: {data.panchak.avoid_activities.join(', ')}</p>}
                  <p className="text-[10px] text-text-muted/60 mt-1">स्रोत: {data.panchak.shastra_ref}</p>
                </div>
              )}
              {data.bhadra && (
                <div className={`rounded-lg p-3 border ${data.bhadra.is_bhadra ? 'bg-red-500/10 border-red-500/30' : 'bg-white/3 border-white/8'}`}>
                  <p className="text-text-muted text-[11px] uppercase">भद्रा</p>
                  <p className="text-text-main">{data.bhadra.is_bhadra ? 'हाँ' : 'नहीं'} {data.bhadra.karana_at_birth ? `· करण: ${data.bhadra.karana_at_birth}` : ''}</p>
                  <p className="text-[10px] text-text-muted/60 mt-1">स्रोत: {data.bhadra.shastra_ref}</p>
                </div>
              )}
              {data.abhijit_muhurta && data.abhijit_muhurta.start && (
                <div className="rounded-lg p-3 border bg-gold/10 border-gold/30">
                  <p className="text-text-muted text-[11px] uppercase">अभिजित मुहूर्त</p>
                  <p className="text-text-main">{data.abhijit_muhurta.start} – {data.abhijit_muhurta.end}</p>
                  <p className="text-[10px] text-text-muted/60 mt-1">स्रोत: {data.abhijit_muhurta.shastra_ref}</p>
                </div>
              )}
              {data.hora && (
                <div className="rounded-lg p-3 border bg-blue-500/10 border-blue-500/30">
                  <p className="text-text-muted text-[11px] uppercase">होरा (जन्म समय)</p>
                  <p className="text-text-main">{data.hora.hora_lord} होरा (#{data.hora.hora_number}) · वार स्वामी: {data.hora.day_lord}</p>
                  <p className="text-[10px] text-text-muted/60 mt-1">स्रोत: {data.hora.shastra_ref}</p>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* PHASE 3 — Astronomy & Planet Intelligence */}
        <Phase3Section data={data} />

        {/* SECTION — Vimshottari Dasha */}
        <Card id="dasha" icon={Clock} title="विंशोत्तरी दशा (Vimshottari Dasha)" testid="card-dasha">
          <div className="space-y-5">
            <CurrentDashaCard
              vimshottari={data.vimshottari}
              onClickLevel={(d, level) => setOpenPopup({ type: 'dasha', payload: { dasha: d, level } })}
            />
            <DashaTimeline
              timeline={data.vimshottari?.timeline || []}
              onClickMD={(md) => setOpenPopup({ type: 'dasha', payload: { dasha: md, level: 'mahadasha' } })}
            />
            <DashaTables
              vimshottari={data.vimshottari}
              onClickRow={(d, level) => setOpenPopup({ type: 'dasha', payload: { dasha: d, level } })}
            />
          </div>
        </Card>

        {/* SECTION — Badhaka */}
        <Card id="badhaka" icon={Shield} title="बाधक (Badhaka)" testid="card-badhaka">
          <BadhakaCard
            badhaka={data.badhaka}
            onClick={() => setOpenPopup({ type: 'badhaka' })}
          />
        </Card>

        {/* SECTION — Chandra Kundli (D1 viewed from Moon's sign as Lagna) */}
        <Card id="chandra" icon={Moon} title="चंद्र कुंडली (Chandra / Moon Lagna)" testid="card-chandra">
          <p className="text-xs text-text-muted mb-4">
            D1 कुंडली · चंद्र की राशि को लग्न मानकर रचित · पारंपरिक रूप से मानसिक प्रवृत्तियों एवं मनोदशा का विश्लेषण इसी से होता है
          </p>
          <div className="flex justify-center">
            <NorthIndianChart
              size={340}
              lagnaSign={data.planetary_positions?.Moon?.sign_idx ?? 0}
              planets={Object.fromEntries(PLANETS.map(nm => {
                const v = data.planetary_positions?.[nm];
                if (!v) return [nm, null];
                const st = data.planetary_status?.[nm] || {};
                return [nm, {
                  sign: v.sign_idx,
                  deg: v.degree,
                  retro: st.is_retrograde,
                  combust: st.is_combust,
                  exalted: st.is_exalted,
                  debilitated: st.is_debilitated,
                }];
              }).filter(([_, v]) => v != null))}
              title={`चंद्र कुंडली · चंद्र राशि ${SIGN_HI[data.planetary_positions?.Moon?.sign_idx ?? 0]}`}
              onClickPlanet={openPlanet}
              onClickLagna={() => setOpenPopup({ type: 'planet', payload: 'Moon' })}
              onClickHouse={openHouse}
              filteredPlanets={filteredPlanets}
              chartId="chandra"
            />
          </div>
        </Card>

        {/* SECTION — Current Transit (Phase B4) */}
        <Card id="transit" icon={Star} title="वर्तमान गोचर (Current Transit)" testid="card-transit">
          {/* Snapshot info + Natal references */}
          <div className="flex flex-wrap items-center gap-3 mb-4 text-xs">
            <span className="text-text-muted">गणना समय:</span>
            <span className="font-mono text-gold">{data.transits?.snapshot_dt_ist || '—'}</span>
            <span className="text-text-muted">·</span>
            <span className="text-text-muted">नाताल लग्न:</span>
            <span className="text-gold">{SIGN_HI[data.transits?.natal_lagna_sign_idx ?? 0]}</span>
            <span className="text-text-muted">·</span>
            <span className="text-text-muted">नाताल चंद्र:</span>
            <span className="text-gold">{SIGN_HI[data.transits?.natal_moon_sign_idx ?? 0]}</span>
          </div>

          {/* Transit chart + Alerts side by side on Desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            {/* Transit Chart — re-use NorthIndianChart with current transit data */}
            <div className="flex flex-col items-center">
              <NorthIndianChart
                size={340}
                lagnaSign={data.transits?.natal_lagna_sign_idx ?? 0}
                houseSigns={Array.from({ length: 12 }, (_, i) => ((data.transits?.natal_lagna_sign_idx ?? 0) + i) % 12)}
                planets={(data.transits?.current || []).reduce((acc, p) => {
                  if (transitFilter === 'slow' && !['Jupiter', 'Saturn', 'Rahu', 'Ketu'].includes(p.name_en)) return acc;
                  if (transitFilter === 'fast' && !['Sun', 'Moon', 'Mars', 'Mercury', 'Venus'].includes(p.name_en)) return acc;
                  if (transitFilter === 'retro' && !p.is_retrograde) return acc;
                  if (transitFilter === 'combust' && !p.is_combust) return acc;
                  acc[p.name_en] = {
                    sign: p.sign_idx,
                    deg: p.degree,
                    retro: p.is_retrograde,
                    combust: p.is_combust,
                    exalted: p.is_exalted,
                    debilitated: p.is_debilitated,
                  };
                  return acc;
                }, {})}
                title="वर्तमान गोचर चक्र (Transit · नाताल लग्न आधारित)"
                onClickPlanet={(nm) => {
                  const planet = (data.transits?.current || []).find(p => p.name_en === nm);
                  if (planet) setOpenPopup({ type: 'transit', payload: planet });
                }}
                chartId="transit"
              />
              {/* Filters */}
              <div className="flex flex-wrap gap-1.5 mt-3 justify-center">
                {[
                  { k: 'all', l: 'सभी ग्रह' },
                  { k: 'slow', l: 'मंद ग्रह' },
                  { k: 'fast', l: 'शीघ्र ग्रह' },
                  { k: 'retro', l: 'केवल वक्री' },
                  { k: 'combust', l: 'केवल अस्त' },
                ].map(f => (
                  <button
                    key={f.k}
                    data-testid={`transit-filter-${f.k}`}
                    onClick={() => setTransitFilter(f.k)}
                    className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-all ${transitFilter === f.k
                      ? 'bg-gold/25 border-gold/60 text-gold'
                      : 'bg-white/5 border-white/10 text-text-muted hover:text-gold'}`}
                  >
                    {f.l}
                  </button>
                ))}
              </div>
            </div>

            {/* Alerts */}
            <div>
              <TransitAlerts alerts={data.transits?.alerts || []} />
            </div>
          </div>

          {/* Transit Table */}
          <TransitTable
            transits={data.transits}
            filter={transitFilter}
            onClickRow={(p) => setOpenPopup({ type: 'transit', payload: p })}
          />
        </Card>

        {/* SECTION — 5-Year Transit Timeline (Phase B5, lazy-loaded) */}
        <Card id="timeline" icon={Hash} title="5-वर्ष गोचर टाइमलाइन (Research)" testid="card-timeline">
          {!timeline && !timelineLoading && (
            <div className="text-center py-8">
              <p className="text-sm text-text-muted mb-4">
                सभी ग्रहों के Sign / Nakshatra / Pada / Retrograde / Combust events आगामी 5 वर्षों के लिए
                <br />
                <span className="text-[11px]">(लगभग 5–7 सेकंड में Swiss Ephemeris से गणना)</span>
              </p>
              <div className="flex justify-center items-center gap-2 mb-3">
                <label className="text-xs text-text-muted">अवधि:</label>
                <select
                  data-testid="timeline-years-select"
                  value={timelineYears}
                  onChange={(e) => setTimelineYears(Number(e.target.value))}
                  className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-sm text-gold"
                >
                  <option value={1}>1 वर्ष</option>
                  <option value={2}>2 वर्ष</option>
                  <option value={3}>3 वर्ष</option>
                  <option value={5}>5 वर्ष</option>
                  <option value={10}>10 वर्ष</option>
                </select>
              </div>
              <button
                data-testid="load-timeline-btn"
                onClick={() => loadTimeline(timelineYears)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gold/20 border border-gold/50 text-gold hover:bg-gold/30 transition-all font-semibold"
              >
                <Hash className="w-4 h-4" /> Timeline लोड करें
              </button>
              {timelineError && <p className="text-rose-300 text-xs mt-3">{timelineError}</p>}
            </div>
          )}

          {timelineLoading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-gold" />
              <p className="text-sm text-text-muted">Swiss Ephemeris पर bisection चल रहा है... कुछ सेकंड लगेंगे</p>
            </div>
          )}

          {timeline && (
            <div className="space-y-6">
              <div>
                <h4 className="font-heading text-base text-gold mb-3">ग्रह अनुसंधान पैनल (Planet Research)</h4>
                <PlanetResearchPanel summary={timeline.planet_summary} />
              </div>
              <div>
                <h4 className="font-heading text-base text-gold mb-3">घटना सूची (Event List)</h4>
                <TimelineView
                  timeline={timeline}
                  onClickEvent={(e) => setOpenPopup({ type: 'timeline_event', payload: e })}
                />
              </div>
            </div>
          )}
        </Card>

        {/* Snapshot Research Mode display */}
        {(snapshotLoading || snapshotMode) && (
          <Card id="snapshot" icon={Sparkles} title={`Research Snapshot ${snapshotMode ? `· ${snapshotMode.date} ${snapshotMode.time} IST` : ''}`} testid="card-snapshot">
            {snapshotLoading && (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
            )}
            {snapshotMode && snapshotMode.data && (
              <div className="space-y-4">
                <div className="flex justify-between flex-wrap gap-2 items-center">
                  <p className="text-xs text-text-muted">यह स्नैपशॉट दिखा रहा है {snapshotMode.date} को सभी ग्रहों की स्थिति. (केवल गणना)</p>
                  <button onClick={() => setSnapshotMode(null)} className="text-xs text-gold hover:text-gold/80 underline">× बंद करें</button>
                </div>
                <div className="overflow-x-auto rounded-xl border border-gold/20">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wider text-gold/80 bg-purple-950/40">
                      <tr>
                        <th className="text-left py-2 px-3">ग्रह</th>
                        <th className="text-left py-2 px-3">राशि</th>
                        <th className="text-left py-2 px-3">अंश</th>
                        <th className="text-left py-2 px-3">नक्षत्र</th>
                        <th className="text-center py-2 px-3">पाद</th>
                        <th className="text-center py-2 px-3">स्थिति</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(snapshotMode.data.current || []).map((p) => (
                        <tr key={p.name_en} className="border-t border-white/5">
                          <td className="py-2 px-3 font-semibold text-gold">{p.name_hi}</td>
                          <td className="py-2 px-3">{p.sign_hi}</td>
                          <td className="py-2 px-3 font-mono text-xs">{p.dms}</td>
                          <td className="py-2 px-3">{p.nakshatra_hi}</td>
                          <td className="py-2 px-3 text-center">{p.pada}</td>
                          <td className="py-2 px-3 text-center text-xs">
                            {p.is_retrograde && '↺ '}
                            {p.is_combust && '🔥 '}
                            {p.is_exalted && '↑ '}
                            {p.is_debilitated && '↓ '}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* SECTION — Varga Charts */}
        <Card id="charts" icon={Star} title="वर्ग कुंडलियाँ (Divisional Charts)" testid="card-charts">
          {/* Varga tabs */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            {vargaOrder.map(code => (
              <button
                key={code}
                data-testid={`varga-tab-${code}`}
                onClick={() => setActiveVarga(code)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${activeVarga === code
                  ? 'bg-gold/25 border-gold/60 text-gold shadow-md shadow-gold/20'
                  : 'bg-white/5 border-white/10 text-text-muted hover:bg-white/10 hover:text-gold'}`}
              >
                {code} <span className="opacity-70 font-normal">{data.varga_charts?.[code]?.name_hi}</span>
              </button>
            ))}
          </div>

          {/* Chart Controls */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button onClick={() => setZoom(z => Math.min(1.8, z + 0.15))} data-testid="zoom-in-btn" aria-label="Zoom in" className="p-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10"><ZoomIn className="w-4 h-4 text-gold" /></button>
            <button onClick={() => setZoom(z => Math.max(0.6, z - 0.15))} data-testid="zoom-out-btn" aria-label="Zoom out" className="p-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10"><ZoomOut className="w-4 h-4 text-gold" /></button>
            <button onClick={() => setZoom(1)} data-testid="zoom-reset-btn" aria-label="Reset zoom" className="p-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10"><RotateCcw className="w-4 h-4 text-gold" /></button>
            <button onClick={() => setFullscreen(f => !f)} data-testid="fullscreen-btn" aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"} className="p-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10">
              {fullscreen ? <Minimize2 className="w-4 h-4 text-gold" /> : <Maximize2 className="w-4 h-4 text-gold" />}
            </button>

            {/* Planet filter */}
            <div className="ml-auto flex items-center gap-1 flex-wrap">
              <Filter className="w-3.5 h-3.5 text-text-muted" />
              {PLANETS.map(p => (
                <button
                  key={p}
                  onClick={() => togglePlanet(p)}
                  data-testid={`filter-${p}`}
                  className={`text-[10px] px-2 py-0.5 rounded-full border ${filteredPlanets.has(p)
                    ? 'bg-gold/15 border-gold/40 text-gold'
                    : 'bg-white/5 border-white/10 text-text-muted line-through opacity-50'}`}
                >
                  {PLANET_HI[p]}
                </button>
              ))}
            </div>
          </div>

          {/* Responsive chart grids */}
          {/* Mobile: 1 chart + swipe arrows */}
          <div className="lg:hidden md:hidden" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <div className="flex items-center justify-between mb-2 px-2">
              <button onClick={() => { const i = Math.max(0, swipeIdx - 1); setSwipeIdx(i); setActiveVarga(vargaOrder[i]); }} className="text-gold/70 hover:text-gold p-1"><ChevronLeft /></button>
              <span className="text-sm text-gold font-semibold">{activeVarga} · {data.varga_charts?.[activeVarga]?.name_hi}</span>
              <button onClick={() => { const i = Math.min(vargaOrder.length - 1, swipeIdx + 1); setSwipeIdx(i); setActiveVarga(vargaOrder[i]); }} className="text-gold/70 hover:text-gold p-1"><ChevronRight /></button>
            </div>
            <div className="flex justify-center">
              <NorthIndianChart
                size={chartSize}
                lagnaSign={dx.lagnaSign}
                houseSigns={dx.houseSigns}
                planets={dx.planets}
                title={`${activeVarga} · ${data.varga_charts?.[activeVarga]?.purpose_hi || ''}`}
                onClickPlanet={openPlanet}
                onClickLagna={openLagna}
                onClickHouse={openHouse}
                filteredPlanets={filteredPlanets}
                showDegrees={activeVarga === 'D1'}
                chartId={activeVarga}
              />
            </div>
            <p className="text-center text-xs text-text-muted mt-2">← Swipe →</p>
          </div>

          {/* Tablet: D1 + D9 side by side */}
          <div className="hidden md:grid lg:hidden grid-cols-2 gap-4 justify-items-center">
            <NorthIndianChart size={chartSize} lagnaSign={d1.lagnaSign} houseSigns={d1.houseSigns} planets={d1.planets} title="D1 · राशि" onClickPlanet={openPlanet} onClickLagna={openLagna} onClickHouse={openHouse} filteredPlanets={filteredPlanets} showDegrees chartId="D1" />
            <NorthIndianChart size={chartSize} lagnaSign={d9.lagnaSign} houseSigns={d9.houseSigns} planets={d9.planets} title="D9 · नवमांश" onClickPlanet={openPlanet} onClickLagna={openLagna} onClickHouse={openHouse} filteredPlanets={filteredPlanets} chartId="D9" />
          </div>

          {/* Desktop: 2x2 grid → D1 | D9 // active varga | nakshatra wheel */}
          <div className="hidden lg:grid grid-cols-2 gap-4 justify-items-center">
            <NorthIndianChart size={chartSize} lagnaSign={d1.lagnaSign} houseSigns={d1.houseSigns} planets={d1.planets} title="D1 · राशि" onClickPlanet={openPlanet} onClickLagna={openLagna} onClickHouse={openHouse} filteredPlanets={filteredPlanets} showDegrees chartId="D1" />
            <NorthIndianChart size={chartSize} lagnaSign={d9.lagnaSign} houseSigns={d9.houseSigns} planets={d9.planets} title="D9 · नवमांश" onClickPlanet={openPlanet} onClickLagna={openLagna} onClickHouse={openHouse} filteredPlanets={filteredPlanets} chartId="D9" />
            {activeVarga !== 'D1' && activeVarga !== 'D9' && (
              <NorthIndianChart size={chartSize} lagnaSign={dx.lagnaSign} houseSigns={dx.houseSigns} planets={dx.planets} title={`${activeVarga} · ${data.varga_charts?.[activeVarga]?.name_hi}`} onClickPlanet={openPlanet} onClickLagna={openLagna} onClickHouse={openHouse} filteredPlanets={filteredPlanets} chartId={activeVarga} />
            )}
          </div>
        </Card>

        {/* SECTION — Nakshatra Wheel */}
        <Card id="wheel" icon={Sparkles} title="नक्षत्र चक्र (Nakshatra Chakra)" testid="card-wheel">
          <p className="text-xs text-text-muted mb-3">
            <span className="text-gold">● Gold</span> = जन्म नक्षत्र · <span className="text-purple-300">● Purple</span> = लग्न नक्षत्र · नक्षत्र पर क्लिक करें विवरण के लिए
          </p>
          <div className="flex justify-center">
            <NakshatraWheel
              size={Math.min(420, chartSize + 60)}
              nakshatras={data.nakshatra_wheel?.nakshatras || []}
              birthIdx={data.nakshatra_wheel?.birth_nakshatra_idx}
              lagnaIdx={data.nakshatra_wheel?.lagna_nakshatra_idx}
              planets={data.nakshatra_wheel?.planets || {}}
              onClickNakshatra={openNakByIdx}
              filteredPlanets={filteredPlanets}
            />
          </div>
        </Card>

        {/* SECTION — Planet Strength */}
        <Card id="strength" icon={BarChart3} title="Basic Planet Strength Score (Experimental)" testid="card-strength">
          <p className="text-xs text-text-muted mb-3">
            ⚠️ <strong>Experimental — Foundational Score Only</strong> · आधार-गणना {'<'} 10 कारकों से: exalted/debilitated/own/mooltrikona/dignity/combust/retro/vargottama/kendra/trikona. 
            <br />यह <em>Shadbala</em> नहीं है। पूर्ण Shadbala (Sthana/Dig/Cheshtaा/Naisargika/Kala/Drik Bala) Phase B7 में आएगा।
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {PLANETS.map(p => {
              const ps = data.planet_strength?.[p];
              if (!ps) return null;
              const score = ps.score;
              const pos = score >= 7 ? 'high' : score >= 3 ? 'mid' : score >= 0 ? 'low' : 'neg';
              const color = pos === 'high' ? 'from-emerald-500/30 to-emerald-700/10 border-emerald-400/40 text-emerald-200'
                : pos === 'mid' ? 'from-gold/30 to-gold/5 border-gold/40 text-gold'
                : pos === 'low' ? 'from-purple-500/30 to-purple-700/10 border-purple-400/40 text-purple-200'
                : 'from-rose-500/30 to-rose-700/10 border-rose-400/40 text-rose-200';
              return (
                <div key={p} className={`rounded-xl p-4 bg-gradient-to-br border ${color}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-heading text-lg">{ps.name_hi}</span>
                    <span className="text-2xl font-mono font-bold">{score}</span>
                  </div>
                  <div className="text-xs space-y-0.5 opacity-90">
                    <div>गरिमा: {ps.components.sign_dignity}</div>
                    <div>भाव: {ps.components.house} {ps.components.is_kendra && '(केंद्र)'} {ps.components.is_trikona && '(त्रिकोण)'}</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {ps.components.exalted && <Tag t="उच्च" />}
                      {ps.components.debilitated && <Tag t="नीच" />}
                      {ps.components.own_sign && <Tag t="स्वराशि" />}
                      {ps.components.mooltrikona && <Tag t="मूलत्रिकोण" />}
                      {ps.components.vargottama && <Tag t="वर्गोत्तम" />}
                      {ps.components.combust && <Tag t="अस्त" />}
                      {ps.components.retrograde && <Tag t="वक्री" />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* SECTION — D1 vs D9 comparison */}
        <Card id="compare" icon={Hash} title="D1 बनाम D9 तुलना" testid="card-compare">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-gold/80 border-b border-gold/20">
                <tr>
                  <th className="text-left py-2 pr-3">ग्रह</th>
                  <th className="text-left py-2 pr-3">D1 राशि</th>
                  <th className="text-left py-2 pr-3">D9 राशि</th>
                  <th className="text-center py-2">वर्गोत्तम</th>
                </tr>
              </thead>
              <tbody>
                {PLANETS.map(p => {
                  const d1Sign = data.varga_charts?.D1?.planets?.[p];
                  const d9Sign = data.varga_charts?.D9?.planets?.[p];
                  const isVarg = d1Sign === d9Sign;
                  return (
                    <tr key={p} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-2 pr-3 font-semibold text-gold">{PLANET_HI[p]}</td>
                      <td className="py-2 pr-3">{d1Sign !== undefined ? SIGN_HI[d1Sign] : '—'}</td>
                      <td className="py-2 pr-3">{d9Sign !== undefined ? SIGN_HI[d9Sign] : '—'}</td>
                      <td className="py-2 text-center">{isVarg ? <span className="text-cyan-300 font-bold">□ हाँ</span> : <span className="text-text-muted">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* SECTION — Planet Master Table */}
        <Card id="planets" icon={Star} title="ग्रह सारणी (Planet Master Table)" testid="card-planets">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-gold/80 border-b border-gold/20">
                <tr>
                  <th className="text-left py-2 pr-3">ग्रह</th>
                  <th className="text-left py-2 pr-3">राशि</th>
                  <th className="text-left py-2 pr-3">अंश (DMS)</th>
                  <th className="text-center py-2 pr-3">भाव</th>
                  <th className="text-left py-2 pr-3">नक्षत्र</th>
                  <th className="text-center py-2 pr-3">पाद</th>
                  <th className="text-left py-2 pr-3">नक्ष-स्वामी</th>
                  <th className="text-left py-2">पाद-स्वामी</th>
                </tr>
              </thead>
              <tbody>
                {PLANETS.map(p => {
                  const v = data.planetary_positions[p];
                  if (!v) return null;
                  return (
                    <tr key={p} onClick={() => openPlanet(p)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer">
                      <td className="py-2 pr-3 font-semibold text-gold">{v.name_hi}</td>
                      <td className="py-2 pr-3">{v.sign_hi}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{v.dms}</td>
                      <td className="py-2 pr-3 text-center">{v.house}</td>
                      <td className="py-2 pr-3">{v.nakshatra}</td>
                      <td className="py-2 pr-3 text-center">{v.pada}</td>
                      <td className="py-2 pr-3">{v.nakshatra_lord_hi}</td>
                      <td className="py-2">{v.pada_lord_hi}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* SECTION 4 — Status */}
        <Card id="status" icon={Sparkles} title="ग्रह स्थिति (Planet Status)" testid="card-status">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {PLANETS.map(p => {
              const s = data.planetary_status[p];
              if (!s) return null;
              const badges = [];
              if (s.is_retrograde) badges.push({ t: '↺ वक्री', c: 'bg-amber-500/15 text-amber-300 border-amber-400/30' });
              if (s.is_combust) badges.push({ t: '🔥 अस्त', c: 'bg-red-500/15 text-red-300 border-red-400/30' });
              if (s.is_exalted) badges.push({ t: '↑ उच्च', c: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30' });
              if (s.is_debilitated) badges.push({ t: '↓ नीच', c: 'bg-rose-500/15 text-rose-300 border-rose-400/30' });
              if (s.is_own_sign) badges.push({ t: '⚜ स्वराशि', c: 'bg-gold/15 text-gold border-gold/30' });
              if (s.is_mooltrikona) badges.push({ t: '✦ मूलत्रिकोण', c: 'bg-indigo-500/15 text-indigo-300 border-indigo-400/30' });
              if (s.is_vargottama) badges.push({ t: '□ वर्गोत्तम', c: 'bg-cyan-500/15 text-cyan-300 border-cyan-400/30' });
              return (
                <div key={p} onClick={() => openPlanet(p)} className="rounded-xl border border-white/10 bg-white/5 p-4 cursor-pointer hover:border-gold/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-heading text-gold">{s.name_hi}</span>
                    <span className="text-xs text-text-muted">{s.motion}</span>
                  </div>
                  <div className="text-xs text-text-muted mb-2">गरिमा: <span className="text-text-main font-medium">{s.sign_dignity}</span></div>
                  <div className="flex flex-wrap gap-1.5">
                    {badges.length === 0 ? <span className="text-xs text-text-muted">—</span> : badges.map((b, i) => (
                      <span key={i} className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${b.c}`}>{b.t}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* SECTION 5 — Relationships (Panchadha Maitri matrix) */}
        <Card id="relations" icon={Hash} title="ग्रह मैत्री (पंचधा मैत्री)" testid="card-relations">
          <p className="text-xs text-text-muted mb-3">Naisargika (नैसर्गिक) + Tatkalika (तात्कालिक) = पंचधा मैत्री</p>
          <div className="overflow-x-auto">
            <table className="text-xs">
              <thead>
                <tr>
                  <th className="px-2 py-1.5 text-left">ग्रह</th>
                  {['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].map(t => (
                    <th key={t} className="px-2 py-1.5 text-center text-gold/70">{data.planetary_relationships?.panchadha?.[t]?.name_hi || t}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].map(p => (
                  <tr key={p} className="border-t border-white/5">
                    <td className="px-2 py-1.5 font-semibold text-gold">{data.planetary_relationships?.panchadha?.[p]?.name_hi || p}</td>
                    {['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].map(t => {
                      if (p === t) return <td key={t} className="px-2 py-1.5 text-center text-text-muted">—</td>;
                      const rel = data.planetary_relationships?.panchadha?.[p]?.table?.[t]?.rel || '—';
                      const color = rel.includes('अधिमित्र') ? 'text-emerald-300' : rel.includes('मित्र') ? 'text-emerald-400/80' : rel.includes('अधिशत्रु') ? 'text-rose-300' : rel.includes('शत्रु') ? 'text-rose-400/80' : 'text-text-muted';
                      return <td key={t} className={`px-2 py-1.5 text-center ${color}`}>{rel}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* SECTION 6 — Bhava Details */}
        <Card id="bhavas" icon={Moon} title="भाव विवरण (12 Houses)" testid="card-bhavas">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-gold/80 border-b border-gold/20">
                <tr>
                  <th className="text-center py-2 pr-3">भाव</th>
                  <th className="text-left py-2 pr-3">राशि</th>
                  <th className="text-left py-2 pr-3">प्रारंभ</th>
                  <th className="text-left py-2 pr-3">मध्य</th>
                  <th className="text-left py-2 pr-3">अंत</th>
                  <th className="text-left py-2">भावेश</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(n => {
                  const b = data.bhavas[String(n)];
                  if (!b) return null;
                  return (
                    <tr key={n} onClick={() => openHouse(n)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer">
                      <td className="py-2 pr-3 text-center font-semibold text-gold">{b.number}</td>
                      <td className="py-2 pr-3">{b.sign_hi}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{b.start_dms}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{b.middle_dms}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{b.end_dms}</td>
                      <td className="py-2">{b.bhava_lord_hi}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* ── Popups ──────────────────────────────────────────────────── */}
      {openPopup?.type === 'planet' && (
        <PlanetPopup
          planet={openPopup.payload}
          data={data}
          onClose={closePopup}
          onClickNakshatra={(name) => setOpenPopup({ type: 'nakshatra', payload: { name } })}
        />
      )}
      {openPopup?.type === 'lagna' && (
        <LagnaPopup data={data} onClose={closePopup} onClickNakshatra={(name) => setOpenPopup({ type: 'nakshatra', payload: { name } })} />
      )}
      {openPopup?.type === 'house' && (
        <HousePopup houseNum={openPopup.payload} data={data} onClose={closePopup} />
      )}
      {openPopup?.type === 'nakshatra' && (
        <NakshatraPopup
          nakshatraIdx={openPopup.payload?.idx}
          nakshatraName={openPopup.payload?.name}
          data={data}
          onClose={closePopup}
        />
      )}
      {openPopup?.type === 'dasha' && (
        <DashaPopup
          dasha={openPopup.payload?.dasha}
          level={openPopup.payload?.level}
          data={data}
          onClose={closePopup}
        />
      )}
      {openPopup?.type === 'badhaka' && (
        <BadhakaPopup data={data} onClose={closePopup} />
      )}
      {openPopup?.type === 'transit' && (
        <TransitPopup planet={openPopup.payload} data={data} onClose={closePopup} />
      )}
      {openPopup?.type === 'timeline_event' && (
        <TimelineEventPopup
          event={openPopup.payload}
          onClose={closePopup}
          onOpenSnapshot={(d, t) => loadResearchSnapshot(d, t)}
        />
      )}
    </div>
  );
}

const Card = ({ id, icon: Icon, title, testid, children }) => (
  <section id={id} data-testid={testid} className="rounded-2xl border border-gold/20 bg-gradient-to-br from-purple-950/30 via-white/[0.02] to-purple-950/30 backdrop-blur-sm p-6 shadow-xl">
    <div className="flex items-center gap-3 mb-5">
      <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center border border-gold/30">
        <Icon className="w-5 h-5 text-gold" />
      </div>
      <h2 className="font-heading text-xl text-gold">{title}</h2>
    </div>
    {children}
  </section>
);

const Grid = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>;
const Divider = () => <div className="my-5 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />;

const Row = ({ label, value, onCopy, highlight }) => (
  <div className={`rounded-xl border border-white/5 px-3 py-2 flex items-center justify-between ${highlight ? 'bg-gold/5 border-gold/20' : 'bg-white/[0.03]'}`}>
    <div>
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={`text-sm ${highlight ? 'text-gold font-semibold' : 'text-text-main'}`}>{value ?? '—'}</div>
    </div>
    {onCopy && value && value !== '—' && (
      <button onClick={() => onCopy(String(value))} className="opacity-50 hover:opacity-100"><Copy className="w-3.5 h-3.5" /></button>
    )}
  </div>
);

const Tag = ({ t }) => <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-black/30 border border-white/20">{t}</span>;
